import loadGoogleMapsApi from "load-google-maps-api";

/**
 * Retrieves Dealer locations from the database
 * Creates Google Map and marker to display the selected location
 */
class Locations {
  constructor(apikey, clientid) {
    this.key = apikey;
    this.clientid = clientid;
    this.gmapsApi = undefined;
    this.mapid = "c569a83e7b22dcd1";
    this.maps = {
      nz: {},
      au: {},
      pi: {},
    };
    this.domref = {};
    this.countries = {
      "New Zealand": "nz",
      Australia: "au",
      "Pacific Islands": "pi",
    };
    Promise.all([this.domReady(), this.getMapsApi()]).then(() => this.init());
  }

  /**
   * Initialization
   */
  init() {
    this.domref = this.getDomRefs();
    this.assignEvents();
    // determine what the default country is on load
    const cc_default = document.body.dataset.country;
    const cc =
      cc_default === "AU"
        ? this.countries["Australia"]
        : this.countries["New Zealand"];
    const tab = this.domref.tab_by_country(cc);
    tab.click();
  }

  /**
   * @async
   * @returns {Promise}
   */
  domReady() {
    if (document.readyState === "complete") {
      return Promise.resolve();
    } else {
      return new Promise((resolve, reject) => {
        document.addEventListener("DOMContentLoaded", () => {
          resolve();
        });
      });
    }
  }

  /**
   * Fetch the google maps API
   * @async
   * @returns {Promise} google.maps api instance
   */
  getMapsApi() {
    if (this.gmapsApi) {
      return Promise.resolve(this.gmapsApi);
    }
    return loadGoogleMapsApi({
      key: this.key,
      libraries: ["places"],
    })
      .then((googleMaps) => {
        this.gmapsApi = googleMaps;
        return this.gmapsApi;
      })
      .catch((e) => {
        // @todo do something when the api can't be loaded
        console.error(e);
      });
  }

  assignEvents() {
    this.domref.tabs.addEventListener("click", (e) => this.showTab(e)); // desktop
    this.domref.countrySelector.addEventListener("change", (e) =>
      this.showTab(e),
    ); // mobile
    this.domref.detail.addEventListener("click", (e) =>
      this.detailOperations(e),
    );
    this.domref.tabpanels_root.addEventListener("click", (e) =>
      this.onAnyTabPanelClick(e),
    );
    this.domref.search_forms.forEach((el) => {
      this.initSearchAutocomplete(el);
      el.addEventListener("submit", (e) => this.onSearchFormSubmit(e));
    });
  }

  /**
   * @todo refactor to use autocomplete with geocoding to save $
   * @param {Element} form search form
   */
  initSearchAutocomplete(el) {
    const cc = el.dataset.cc;
    const input = el.querySelector(".form__input");
    const opts = {
      fields: ["geometry.location"], // Basic Data SKU @ $0 additional charge
      types: ["(regions)"],
      strictBounds: false,
      componentRestrictions: {
        country: cc,
      },
    };
    // Country restrictions for Pacific Islands requires custom bounds to contain
    // all inclusive islands and regions, adjust as necessary.
    if (cc === "pi") {
      opts.bounds = new this.gmapsApi.LatLngBounds(
        new this.gmapsApi.LatLng([-32.837280016676814, 121.6829227166418]), // ne
        new this.gmapsApi.LatLng([34.094376449841086, -141.2858295592956]), // sw
      );
      delete opts.componentRestrictions;
    }
    const ac = new this.gmapsApi.places.Autocomplete(input, opts);
    ac.addListener("place_changed", () => {
      const place = ac.getPlace();
      if (place.length === 0 || !place.geometry) {
        return;
      }
      this.spinner(input, "show", "sm", "white");
      const lens = el.querySelector(".search__icon");
      lens.style.display = "none";
      const latlng = `${place.geometry.location.lat()},${place.geometry.location.lng()}`;
      this.fetchLocations(cc, "latlng", latlng).then(() => {
        this.refreshMap(cc);
        this.spinner(input, "hide");
        lens.style.display = "initial";
      });
    });
  }

  /**
   * Assign DOM refs
   * @returns {object}
   */
  getDomRefs() {
    return {
      tabs: document.querySelector(".locations__tabs"),
      tabpanels_root: document.querySelector(".locations__tab-panels"),
      tabbuttons: document.querySelectorAll(".locations__tabs li"),
      tabpanels: document.querySelectorAll(".locations__tab"),
      countrySelector: document.querySelector(
        ".locations__country-select--select",
      ),
      list: document.querySelectorAll(".locations__list"),
      detail: document.querySelector(".location-detail"),
      location_dropdowns: document.querySelectorAll(
        ".location-detail__dropdown",
      ),
      spinner: document.getElementById("loader-spinner"),
      get search_forms() {
        return this.tabpanels_root.querySelectorAll(".search__form");
      },
      get search_inputs() {
        return this.tabpanels_root.querySelectorAll('input[type="search"]');
      },
      infoWindow: {
        root: document.getElementById("locations-list__infowindow")
          .firstElementChild,
        get title() {
          return this.root.querySelector(".locations-list__infowindow--title");
        },
        get addr() {
          return this.root.querySelector(
            ".locations-list__infowindow--address",
          );
        },
        get phone() {
          return this.root.querySelector(".locations-list__infowindow--phone");
        },
        get button() {
          return this.root.querySelector("button");
        },
      },
      tabpanel(cc) {
        return document.querySelector(`.locations__tab--${cc}`);
      },
      tab_by_country(cc) {
        return document.querySelector(`li[data-tab='${cc}']`);
      },
      tabpanel_list(cc) {
        return document.querySelector(
          `.locations__tab--${cc} .locations__list`,
        );
      },
      locations(cc) {
        return this.tabpanel(cc).querySelectorAll(".locations-list__location");
      },
      search_form(cc) {
        return this.tabpanel(cc).querySelector(".search__form");
      },
    };
  }

  /**
   * Enter key or submit button on search field.
   * This is a fallback in case autocomplete does not work.
   * @param {Event} e
   */
  onSearchFormSubmit(e) {
    e.preventDefault();
    const el = e.target; // should always expect FORM
    const cc = el.dataset.cc;
    const q = el.querySelector('input[type="search"]').value;
    this.fetchLocations(cc, "query", q).then(() => this.refreshMap(cc));
  }

  /**
   * Retrieves list of locations in a country, region or by query
   * @async
   * @param {string} cc country code (required)
   * @param {string} prop one of 'region', 'query', or 'latlng'. leave empty for cc.
   * @param {string} val property value. leave empty for cc
   * @returns {Promise}
   */
  fetchLocations(cc, prop = "", val = "") {
    let ep;
    switch (prop) {
      case "latlng":
        ep = `radius/?latlng=${val}`;
        break;
      case "query":
        // au, nz are region bounds. pi is a lat/lng bounds *best guess* edit as necessary.
        const r = cc === "pi" ? "" : `&r=${cc}`;
        const b =
          cc !== "pi"
            ? ""
            : "&b=-32.837280016676814,121.6829227166418|34.094376449841086,-141.2858295592956";
        ep = `query/?s=${val}${r}${b}`;
        break;
      case "region":
        ep = `region/${val}`;
        break;
      default:
        ep = `country/${cc}`;
    }
    return fetch(`locationapi/getlocations/${ep}`, {
      headers: { Accept: "application/json" },
    })
      .then((res) => res.json())
      .then((data) => {
        this.maps[cc].locations = data.Locations;
        // Refresh the locations panel
        const el = this.domref.tabpanel_list(cc);
        if (data.Locations.length) {
          el.innerHTML = data.PanelHtml;
        } else {
          // @todo move styles to scss
          el.innerHTML =
            '<i style="padding:15px;display:block;">No locations found within a 200 kilometer radius.</i>';
        }
        el.scrollTop = 0;
      })
      .catch(console.error); // @todo do something on error
  }

  /**
   * Capture clicks (only clicks) on all tab panels then delegate to handlers
   * @param {Event} e
   */
  onAnyTabPanelClick(e) {
    const el = e.target;
    switch (el.tagName) {
      case "A":
        if (el.classList.contains("locations-list__location--marker")) {
          this.invokeMarkerClick(e);
          break;
        }
        if ("location" in el.dataset) {
          e.preventDefault();
          const table_link = el.classList.contains("locations__table--dealer");
          this.spinner(el, "show", table_link ? "xs" : "xxs");
          this.showDetail(el.dataset.location).then(() =>
            this.spinner(el, "hide"),
          );
          break;
        }
        if ("region" in el.dataset) {
          e.preventDefault();
          const cc = this.countries[el.dataset.country];
          this.domref.search_form(cc).querySelector(".search__input").value =
            "";
          this.spinner(el, "show", "xs");
          this.fetchLocations(cc, "region", el.dataset.region).then(() => {
            window.scrollTo({ top: 360, behavior: "smooth" });
            this.refreshMap(cc);
            this.spinner(el, "hide");
          });
          break;
        }
        if (el.classList.contains("search__link--mylocation")) {
          this.onUseMyLocationClick(e);
          break;
        }
        break;
      case "BUTTON":
        if ("location" in el.dataset) {
          const button = this.domref.infoWindow.button;
          button.disabled = true;
          this.spinner(button, "show", "sm");
          this.showDetail(el.dataset.location).then(() => {
            button.disabled = false;
            this.spinner(button, "hide");
          });
          break;
        }
    }
  }

  /**
   * Attempt to geolocate the customer's location at their request
   * @param {Event} e
   */
  onUseMyLocationClick(e) {
    e.preventDefault();
    const el = e.target;
    const cc = el.dataset.cc;
    const msg_el = el.parentElement.querySelector(".search__link--msg");
    this.domref.search_form(cc).querySelector(".search__input").value = "";
    if ("geolocation" in navigator) {
      this.spinner(el, "show", "xs");
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const latlng = `${pos.coords.latitude},${pos.coords.longitude}`;
          this.fetchLocations(cc, "latlng", latlng).then(() =>
            this.refreshMap(cc),
          );
          msg_el.textContent = "";
          this.spinner(el, "hide");
        },
        (err) => {
          msg_el.textContent = "Unable to retrieve your location.";
          this.spinner(el, "hide");
        },
        {
          timeout: 6000,
        },
      );
    } else {
      msg_el.textContent = "Geolocation is not enabled on your device.";
    }
  }

  /**
   * Create a google map and initialize with defaults
   * @param {string} cc country code (nz/au/pi)
   * @returns {google.maps.Map} instance
   */
  getMap(cc) {
    if (!this.maps[cc].hasOwnProperty("gmap")) {
      this.maps[cc].gmap = new this.gmapsApi.Map(
        document.getElementById(`${cc}_map`),
        {
          mapId: this.mapid,
          zoom: 12,
          zoomControl: true,
          fullscreenControl: true,
          mapTypeControl: false,
          scaleControl: false,
          streetViewControl: false,
          rotateControl: false,
        },
      );
    }
    return this.maps[cc].gmap;
  }

  /**
   * Create or return an instance of an infoWindow
   * @param {string} cc country code
   * @returns {google.maps.InfoWindow} instance
   */
  getInfoWindow(cc) {
    if (!this.maps[cc].hasOwnProperty("infowindow")) {
      this.maps[cc].infowindow = new this.gmapsApi.InfoWindow({
        minWidth: 310,
        maxWidth: 320,
      });
    }
    return this.maps[cc].infowindow;
  }

  /**
   * Show appropriate Tab content when tab is clicked or on page load
   * @todo close any open infowindows when switching tabs, to fix a bug where infowindow content disappers
   * @param {Event} e
   */
  showTab(e) {
    const el = e.target;
    const cc = this.getSelectedCountry(el);
    if (cc) {
      // Highlight selected tab
      this.domref.tabbuttons.forEach((tb) => {
        tb.classList.remove("selected");
      });
      this.domref.tabpanels.forEach((tp) => {
        tp.classList.remove("locations__tab--show");
      });
      el.classList.add("selected");
      const currentTab = this.domref.tabpanel(cc);
      currentTab.classList.add("locations__tab--show");
      this.getInfoWindow(cc).close();
      // Get initial list of locations for country when the locations panel is empty
      if (this.domref.locations(cc).length === 0) {
        const map_el = currentTab.querySelector(".locations__tab--map");
        this.spinner(map_el, "show", "xl");
        this.fetchLocations(cc).then(() => {
          this.refreshMap(cc);
          this.spinner(map_el, "hide");
        });
      }
    }
  }

  /**
   * Gets the selected country from current tab or select field.
   * @param {Element} el list item (tab) or select field
   * @returns {string} nz, au or pi
   */
  getSelectedCountry(el) {
    switch (el.tagName) {
      case "LI": // used for desktop
        return el.dataset.tab;
      case "SELECT": // used for mobile
        return el.value;
    }
  }

  /**
   * Loads appropriate Google Map for the selected region
   * @param {string} cc country code
   */
  refreshMap(cc) {
    const map = this.getMap(cc);
    this.maps[cc].markers = {};
    // const infoWindow = new this.gmapsApi.InfoWindow({
    //   minWidth: 310,
    //   maxWidth: 320
    // });
    const bounds = new this.gmapsApi.LatLngBounds();
    for (let i = 0; i < this.maps[cc].locations.length; i++) {
      let l = this.maps[cc].locations[i];
      if (!l.LatLng) {
        continue; // some dealers don't have this data
      }
      const llArr = l.LatLng.split(",");
      const params = Object.assign(l, {
        latlngObj: new this.gmapsApi.LatLng(
          parseFloat(llArr[0]),
          parseFloat(llArr[1]),
        ),
        cc,
      });
      bounds.extend(params.latlngObj);
      this.maps[cc].markers[l.ID] = this.addMarker(params);
    }
    map.fitBounds(bounds);
    map.setCenter(bounds.getCenter());
  }

  /**
   * Show or hide a loading spinner
   * @param {Element} el
   * @param {string} action show or hide
   * @param {string} size  xxs, xs, sm, md, lg, or xl
   * @param {string} tint dark, white
   */
  spinner(el, action, size = "md", tint = "dark") {
    let sp;
    if (action === "show") {
      sp = this.domref.spinner.cloneNode();
      sp.removeAttribute("id");
      sp.classList.add(`brLoader--${size}`, `brLoader--${tint}`);
      sp.style.display = "initial";
      el.after(sp);
    } else {
      sp = el.nextElementSibling;
      if (sp.classList.contains("brLoader")) {
        sp.parentNode.removeChild(sp);
      }
    }
  }

  /**
   * Programatically invoke a map marker click
   * @param {Event} e
   */
  invokeMarkerClick(e) {
    e.preventDefault();

    const nzMapElement = document.getElementById("nz_map");
    function isMobile() {
      return window.innerWidth <= 768;
    }
    if (nzMapElement && isMobile()) {
      nzMapElement.scrollIntoView({ behavior: "smooth" });
    }

    const auMapElement = document.getElementById("au_map");
    if (auMapElement && isMobile()) {
      auMapElement.scrollIntoView({ behavior: "smooth" });
    }

    const locId = e.target.dataset.location;
    const cc = this.countries[e.target.dataset.country];

    const ll = e.target.dataset.latlng.split(",");
    const latlngObj = new this.gmapsApi.LatLng(
      parseFloat(ll[0]),
      parseFloat(ll[1]),
    );

    if (this.maps[cc]) {
      if (this.maps[cc].markers) {
        const marker = this.maps[cc].markers[locId];
        this.gmapsApi.event.trigger(marker, "click");
      }
    }

    this.getMap(cc).panTo(latlngObj);
  }

  /**
   * Add a marker and attach an infoWindow handler
   * @param {Object} params
   * @return {google.maps.Marker} instance
   */
  addMarker(params) {
    const marker = new this.gmapsApi.Marker({
      position: params.latlngObj,
      map: this.getMap(params.cc),
      icon: "/static/img/H-marker-pin-brand-black.svg",
    });
    marker.addListener("click", (e) => {
      //const addr = params.StreetAddress.replace(/(?:\r\n|\r|\n)/g, '<br>');
      const addr = params.StreetAddress;
      const iw = this.domref.infoWindow;

      iw.addr.innerHTML = addr;

      if (params.cc == "nz" && params.ShowAddress == 1) {
        iw.addr.innerHTML = "";
      }

      iw.title.textContent = params.Title;
      iw.phone.textContent = params.Phone;
      iw.button.dataset.location = params.ID;
      const infow = this.getInfoWindow(params.cc);
      infow.setContent(iw.root);
      infow.open({
        anchor: marker,
        map: this.getMap(params.cc),
        shouldFocus: true,
      });
    });
    return marker;
  }

  /**
   * Shows modal with details of the selected location
   * @async
   * @param {*} locId location id
   * @returns {Promise}
   */
  showDetail(locId) {
    if (!locId) {
      return Promise.resolve();
    }
    const url = `/locationapi/locationdetail/${locId}`;
    return fetch(url, {
      headers: { Accept: "text/html" },
    })
      .then((res) => res.text())
      .then((data) => {
        window.open(data, "_blank");
        //this.domref.detail.innerHTML = data;
        //this.domref.detail.classList.add('modal--show');
      });
  }

  /**
   * Event handlers for the location details modal
   * @param {*} e
   */
  detailOperations(e) {
    // Close modal button clicked
    if (
      e.target.classList.contains("modal--close") ||
      e.target.classList.contains("modal")
    ) {
      const modal = e.target.closest(".modal");
      modal.classList.remove("modal--show");
      modal.innerHTML = "";
    }
    // Mobile dropdown clicked
    if (e.target.closest(".location-detail__dropdown")) {
      const dropdowns = this.domref.location_dropdowns;
      if (e.target.closest(".location-detail__dropdown--show")) {
        e.target
          .closest(".location-detail__dropdown--show")
          .classList.remove("location-detail__dropdown--show");
        return;
      }
      dropdowns.forEach((d) => {
        d.classList.remove("location-detail__dropdown--show");
      });
      e.target
        .closest(".location-detail__dropdown")
        .classList.add("location-detail__dropdown--show");
    }
  }
}

export default Locations;
